// A one-shot signal that lets a user-initiated skip gesture (transport /
// media-session next-previous) tell the metrics binding that the imminent
// `current` change is a deliberate skip, not a natural `ended`→advance.
//
// WHY a module-level signal rather than a param threaded through the
// controller: `skipNext()`/`skipPrevious()` are shared by the natural
// `ended` auto-advance and by user gestures, so the controller itself can't
// tell them apart. Stamping intent at the gesture callsite and reading it at
// the metrics seam keeps the transport path untouched and the metric purely
// observational.

import { markInteractionStart } from '@/perf/playbackDelayMarks'

export type SkipDirection = 'next' | 'previous'

let pendingSkip: SkipDirection | null = null
let expiryHandle: ReturnType<typeof setTimeout> | null = null

// WHY expire on the next macrotask: the skip's `current` change lands within
// the gesture's microtask chain (the prefetched `next` promise resolves
// synchronously-ish), so the consumer always runs first. But a skip at the
// queue boundary no-ops without changing `current`, leaving the mark unread —
// self-clearing on the next macrotask stops it being wrongly attributed to a
// later, unrelated track change (e.g. the natural end-of-track advance).
function armExpiry(): void {
    if (expiryHandle != null) clearTimeout(expiryHandle)
    expiryHandle = setTimeout(() => {
        pendingSkip = null
        expiryHandle = null
    }, 0)
}

/**
 * Called by a user-skip callsite immediately before `skip{Next,Previous}()`.
 * Every user-triggered skip (transport next/previous buttons + swipe, keyboard,
 * media keys, media-session, audiobook chapter nav, overflow "skip to previous")
 * routes through here; the natural `ended`→advance and autoplay continuation do
 * NOT, so this is the single seam that distinguishes a user skip from a machine
 * advance. A user skip starts a NEW track, so also open the perf journey window
 * here — its interaction→playing bar closes at the imminent track's `playing`
 * (a skip reuses the live queue and never reaches markPlayStart, so this is the
 * window's only opener). A no-op boundary skip leaves the window to the staleness
 * guard, same as the metrics mark self-clears.
 */
export function markUserSkip(direction: SkipDirection): void {
    pendingSkip = direction
    armExpiry()
    markInteractionStart()
}

/** Reads and clears the pending direction; null when the advance wasn't a user skip. */
export function consumeUserSkip(): SkipDirection | null {
    const direction = pendingSkip
    pendingSkip = null
    if (expiryHandle != null) {
        clearTimeout(expiryHandle)
        expiryHandle = null
    }
    return direction
}
